import { useCallback, useState } from "react";

import toast from "react-hot-toast";

import { BanknoteIcon, FileTextIcon, SearchIcon } from "lucide-react";

import { formatDate } from "simplydo/core/dates";
import type { OpenAPI } from "simplydo/interfaces";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import { H3 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import { Loader } from "simplydo/react/ui/loader";
import { Message, MessageHeader } from "simplydo/react/ui/message";

import api from "api";

import type {
  CompanyCreditsafe,
  CreditRatingValue,
} from "components/innointel/types";

import { InfoBoxWidget, InfoBoxWidgetTitleArea, KeyFact } from "../Styles";
import CreditsafeManualMatch from "./CreditsafeManualMatch";

const CreditRatingColourMap: Record<CreditRatingValue, string> = {
  A: "MediumSeaGreen",
  B: "MediumSeaGreen",
  C: "MediumSeaGreen",
  D: "Orange",
  E: "Orange",
};

const getCreditRatingColour = (creditRating: string) =>
  creditRating in CreditRatingColourMap
    ? CreditRatingColourMap[creditRating as CreditRatingValue]
    : "red";

const ReportCallout = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px 16px;
  padding: 14px 16px;
  border: 1px solid #e3e6ea;
  border-radius: 8px;
  background: #f5f6f8;

  .text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1 1 240px;
    min-width: 0;
  }

  .title {
    font-weight: 600;
    color: #111418;
  }

  .hint {
    font-size: 13px;
    color: #5b6470;
  }
`;

const CreditContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const CreditRatingDisplay = styled.div<{ $creditRating: string }>`
  border-radius: 50%;
  height: 65px;
  width: 65px;
  background-color: ${({ $creditRating }) =>
    getCreditRatingColour($creditRating)};
  display: flex;
  justify-content: center;
  align-items: center;
  > span {
    display: block;
    color: #fff;
    font-weight: bold;
    font-size: 2.5em;
  }
`;

type CreditsafeReportSummaryProps = { creditsafe: CompanyCreditsafe };

export const CreditsafeReportSummary = ({
  creditsafe,
}: CreditsafeReportSummaryProps) => {
  const reportData = creditsafe.report?.data;
  const currentCreditRating = reportData?.creditScore?.currentCreditRating;
  const latestRatingChangeDate =
    reportData?.creditScore?.latestRatingChangeDate;
  const commonValue = currentCreditRating?.commonValue ?? "";

  if (!currentCreditRating?.commonValue) {
    return null;
  }

  return (
    <CreditContainer>
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          gap: 12,
        }}
      >
        <CreditRatingDisplay $creditRating={commonValue}>
          <span>{commonValue}</span>
        </CreditRatingDisplay>
        <KeyFact
          title="Credit rating"
          style={{ marginBottom: 0 }}
          fact={
            <>
              Rating: {currentCreditRating.commonValue},{" "}
              {currentCreditRating.commonDescription}.<br />
              {latestRatingChangeDate ? (
                <span style={{ color: "gray", fontSize: "0.9rem" }}>
                  Last updated{" "}
                  {formatDate(latestRatingChangeDate, "MMM d, yyyy p")}
                </span>
              ) : null}
            </>
          }
        />
      </div>
    </CreditContainer>
  );
};

type CompanyCreditsafeProps = {
  companyId: string;
  name: string;
  location?: OpenAPI.Schemas["InnointelCompanies"]["location"];
  creditsafe: CompanyCreditsafe;
  company: OpenAPI.Schemas["InnointelCompanies"];
};

const CompanyCreditsafe = ({
  companyId,
  name,
  location,
  creditsafe,
  company,
}: CompanyCreditsafeProps) => {
  const [reportLoading, setReportLoading] = useState(false);
  const [manualMatchOpen, setManualMatchOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [creditsafeErrorMessage, setCreditsafeErrorMessage] = useState("");

  const getReportData = useCallback(() => {
    setReportLoading(true);
    api.innointel.getCreditsafeReport(
      companyId,
      "",
      ({ report, needsMatch }) => {
        setReportLoading(false);
        if (needsMatch) {
          toast("Find this company on Creditsafe first to get its report.");
          setManualMatchOpen(true);
        } else if (report) {
          api.invalidateQueries(api.innointel.getCompany, [companyId]);
        } else {
          toast.error("Creditsafe has no detailed report for this company.");
        }
      },
      (err) => {
        toast.error(err.message);
        setCreditsafeErrorMessage(err.message);
        setReportLoading(false);
      },
    );
  }, [companyId]);

  const getReportPdf = useCallback(() => {
    setPdfLoading(true);
    api.innointel.getCreditsafeReportPdf(
      companyId,
      ({ report, didFindReport: didFind }) => {
        setPdfLoading(false);
        if (didFind) {
          window.open(report.path, "_blank");
          api.invalidateQueries(api.innointel.getCompany, [companyId]);
        } else {
          toast.error("Could not fetch detailed report.");
        }
      },
      (err) => {
        toast.error(err.message);
        setPdfLoading(false);
      },
    );
  }, [companyId]);

  return (
    <InfoBoxWidget $relaxed={!!creditsafeErrorMessage}>
      <CreditsafeManualMatch
        isOpen={manualMatchOpen}
        onClose={() => setManualMatchOpen(false)}
        companyId={companyId}
        name={name}
        location={location}
        onError={(err) => {
          setCreditsafeErrorMessage(err);
          setManualMatchOpen(false);
        }}
      />

      {creditsafeErrorMessage ? (
        <Message color="red">
          <MessageHeader>Could not fetch credit information</MessageHeader>
          <p>
            CreditSafe is currently unable to process this request. We are
            unable to fetch credit information at this time. Please try again
            later or contact us directly if this issue persists.
          </p>
          <div
            style={{
              width: "100%",
              display: "flex",
              justifyContent: "flex-end",
              flexDirection: "row",
            }}
          >
            <Button
              size="small"
              onClick={() => {
                setCreditsafeErrorMessage("");
              }}
              variant="outline"
            >
              Try again
            </Button>
          </div>
        </Message>
      ) : (
        <>
          <InfoBoxWidgetTitleArea $noMargin>
            <Icon icon={BanknoteIcon} />
            <H3>Credit information</H3>
          </InfoBoxWidgetTitleArea>
          {creditsafe.name ? (
            <div>
              <KeyFact title="Company name" fact={creditsafe.name} />
            </div>
          ) : null}
          {creditsafe.status ? (
            <div>
              <KeyFact title="Company status" fact={creditsafe.status} />
            </div>
          ) : null}
          {creditsafe.vatNo ? (
            <div>
              <KeyFact title="VAT number" fact={creditsafe.vatNo} />
            </div>
          ) : null}
          {creditsafe.safeNo ? (
            <div>
              <KeyFact title="SAFE number" fact={creditsafe.safeNo} />
            </div>
          ) : null}
          {!creditsafe.report ? (
            creditsafe.id && company.status === "processing" ? (
              <ReportCallout>
                <div className="text">
                  <span className="title">Looking up credit information</span>
                  <span className="hint">
                    This appears once Creditsafe has answered.
                  </span>
                </div>
                <Loader size="small" style={{ margin: 0 }} />
              </ReportCallout>
            ) : creditsafe.name ? (
              <ReportCallout>
                <div className="text">
                  <span className="title">Detailed credit report</span>
                  <span className="hint">
                    Credit rating, shareholders and financial health from
                    Creditsafe.
                  </span>
                </div>
                <Button
                  size="small"
                  color="primary"
                  loading={reportLoading}
                  onClick={getReportData}
                >
                  <Icon icon={FileTextIcon} />
                  Get credit report
                </Button>
              </ReportCallout>
            ) : (
              <ReportCallout>
                <div className="text">
                  <span className="title">Not linked to Creditsafe</span>
                  <span className="hint">
                    Find this company on Creditsafe to see its credit details.
                  </span>
                </div>
                <Button
                  size="small"
                  variant="outline"
                  onClick={() => setManualMatchOpen(true)}
                >
                  <Icon icon={SearchIcon} />
                  Find on Creditsafe
                </Button>
              </ReportCallout>
            )
          ) : (
            <>
              <CreditsafeReportSummary creditsafe={creditsafe} />
              <div>
                <Button
                  size="small"
                  variant="outline"
                  loading={pdfLoading}
                  onClick={getReportPdf}
                >
                  <Icon icon={FileTextIcon} />
                  Download full report (PDF)
                </Button>
              </div>
            </>
          )}
        </>
      )}
    </InfoBoxWidget>
  );
};

export default CompanyCreditsafe;
